import { FolderPlus, Save, Key } from 'lucide-react'
import { useState } from 'react'
import UploadInterface from './upload-interface'
import NotificationModal from './ui/notification-modal'
import { useRedeemOTP } from '../lib/hooks'

interface FolderToolbarProps {
  /**
   * The Memoir this workspace is scoped to (SOL-204). Absent where nothing can
   * be created — the "Shared with you" view, whose albums belong to someone
   * else's Memoir — so upload and New Album are hidden there.
   */
  memoirId?: string
  onCreateFolder: () => void
  onSaveChanges: () => void
  onUploadComplete: () => void
  hasUnsavedChanges?: boolean
  className?: string
}

interface NotificationState {
  isOpen: boolean
  title: string
  message: string
  variant: 'success' | 'error' | 'info'
}

export default function FolderToolbar({
  memoirId,
  onCreateFolder,
  onSaveChanges,
  onUploadComplete,
  hasUnsavedChanges = false,
  className = ''
}: FolderToolbarProps): JSX.Element {
  const [redeemModalOpen, setRedeemModalOpen] = useState(false)
  const [otpCode, setOtpCode] = useState('')
  const [validationError, setValidationError] = useState('')
  const { redeemOTP, loading: redeemLoading, error: redeemError } = useRedeemOTP()

  const [notification, setNotification] = useState<NotificationState>({
    isOpen: false,
    title: '',
    message: '',
    variant: 'info'
  })

  const showNotification = (variant: 'success' | 'error' | 'info', title: string, message: string) => {
    setNotification({ isOpen: true, variant, title, message })
  }

  const closeNotification = () => {
    setNotification(prev => ({ ...prev, isOpen: false }))
  }

  const handleRedeemSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setValidationError('')

    if (!otpCode.trim()) {
      setValidationError('Please enter an OTP code')
      return
    }

    if (otpCode.length !== 8) {
      setValidationError('Code must be exactly 8 characters')
      return
    }

    try {
      const response = await redeemOTP({ otp: otpCode.trim().toUpperCase() })

      // Reset and close modal
      setOtpCode('')
      setRedeemModalOpen(false)

      // Show success message
      showNotification('success', 'Album Added!', `"${response.album.name}" has been added to your collection.`)

      // Trigger refresh
      onUploadComplete()
    } catch (error) {
      console.error('Failed to redeem OTP:', error)
      showNotification('error', 'Failed to Add Album', redeemError || 'Invalid or expired code. Please try again.')
    }
  }

  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Auto-uppercase and limit to 8 characters
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8)
    setOtpCode(value)
    setValidationError('')
  }

  const handleCloseRedeemModal = () => {
    setRedeemModalOpen(false)
    setOtpCode('')
    setValidationError('')
  }

  return (
    <>
      <div className={`folder-toolbar ${className}`}>
        <div className="folder-toolbar-left">
          {memoirId && (
            <>
              <UploadInterface memoirId={memoirId} onUploadComplete={onUploadComplete} />
              <button
                className="folder-action-button"
                onClick={onCreateFolder}
                title="Create new album"
              >
                <FolderPlus size={16} />
                <span>New Album</span>
              </button>
            </>
          )}
          <button
            className="folder-action-button"
            onClick={() => setRedeemModalOpen(true)}
            title="Add album via code"
          >
            <Key size={16} />
            <span>Add Album via Code</span>
          </button>
        </div>

        <div className="folder-toolbar-right">
          {hasUnsavedChanges && (
            <button
              className="folder-save-button"
              onClick={onSaveChanges}
              title="Save changes"
            >
              <Save size={16} />
              <span>Save Changes</span>
            </button>
          )}
        </div>
      </div>

      {/* OTP Redeem Modal */}
      {redeemModalOpen && (
        <div className="modal-overlay" onClick={handleCloseRedeemModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Add Album via Code</h3>
            <p>Enter the 8-character code you received to access a shared album</p>

            <form onSubmit={handleRedeemSubmit}>
              <div className="form-group">
                <label htmlFor="otp-input">Album Code</label>
                <input
                  id="otp-input"
                  type="text"
                  value={otpCode}
                  onChange={handleOtpChange}
                  placeholder="Enter 8-character code"
                  className="otp-input"
                  maxLength={8}
                  autoFocus
                  disabled={redeemLoading}
                />
                <small>Code should be 8 characters (letters and numbers)</small>
              </div>

              {(validationError || redeemError) && (
                <div className="error-message">
                  <p>{validationError || redeemError}</p>
                </div>
              )}

              <div className="modal-actions">
                <button
                  type="submit"
                  disabled={redeemLoading || otpCode.length !== 8}
                  className="primary-button"
                >
                  {redeemLoading ? 'Adding Album...' : 'Add Album'}
                </button>
                <button
                  type="button"
                  onClick={handleCloseRedeemModal}
                  disabled={redeemLoading}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Notification Modal */}
      <NotificationModal
        isOpen={notification.isOpen}
        title={notification.title}
        message={notification.message}
        variant={notification.variant}
        onClose={closeNotification}
      />
    </>
  )
}
